import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown, MessageSquareText, Send, MessageCircle, PhoneCall, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CTALink } from "@/components/common/CTALink";
import { SOLUTIONS } from "@/data/site";
import { cn } from "@/lib/utils";

const iconMap = { MessageSquareText, Send, MessageCircle, PhoneCall };

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Industries", to: "/#industries" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-2.5 group" aria-label="E Telecom home">
    <img
      src="/etelecom-logo.png"
      alt="E Telecom logo"
      className="h-9 sm:h-10 w-auto object-contain"
      width="140"
      height="44"
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      )}
    >
      <div className="et-container flex h-16 lg:h-[72px] items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavItem to="/">Home</NavItem>
          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button
              data-testid="nav-solutions-trigger"
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-expanded={solutionsOpen}
              onClick={() => setSolutionsOpen((v) => !v)}
            >
              Solutions
              <ChevronDown className={cn("h-4 w-4 transition-transform", solutionsOpen && "rotate-180")} />
            </button>
            {solutionsOpen && (
              <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3 w-[560px]">
                <div className="et-card p-3 grid grid-cols-2 gap-1.5">
                  {SOLUTIONS.map((s) => {
                    const Icon = iconMap[s.icon] || MessageSquareText;
                    return (
                      <Link
                        key={s.slug}
                        to={s.path}
                        data-testid={`nav-solution-${s.slug}`}
                        className="flex items-start gap-3 rounded-xl p-3 hover:bg-slate-50 transition-colors group"
                      >
                        <span className="et-icon-box shrink-0"><Icon className="h-5 w-5" /></span>
                        <span>
                          <span className="block text-sm font-semibold text-slate-900">{s.title}</span>
                          <span className="block text-xs text-slate-500 leading-snug mt-0.5">{s.short}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <NavItem to="/#industries">Industries</NavItem>
          <NavItem to="/about">About Us</NavItem>
          <NavItem to="/contact">Contact</NavItem>
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <CTALink to="/contact" variant="secondary" size="md">Talk to an Expert</CTALink>
          <CTALink to="/pricing" variant="primary" size="md" data-testid="nav-get-quote-button" withArrow>Get Started</CTALink>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-button"
                aria-label="Open menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm p-0 overflow-y-auto">
              <div className="p-5 border-b border-slate-200"><Logo /></div>
              <div className="p-3">
                <MobileLink to="/">Home</MobileLink>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="solutions" className="border-none">
                    <AccordionTrigger className="px-3 py-3 text-sm font-medium hover:no-underline rounded-lg hover:bg-slate-50">
                      Solutions
                    </AccordionTrigger>
                    <AccordionContent className="pb-1">
                      <div className="flex flex-col gap-1 pl-2">
                        {SOLUTIONS.map((s) => {
                          const Icon = iconMap[s.icon] || MessageSquareText;
                          return (
                            <SheetClose asChild key={s.slug}>
                              <Link to={s.path} data-testid={`mnav-solution-${s.slug}`} className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                                <Icon className="h-4 w-4 text-[var(--et-primary)]" /> {s.title}
                              </Link>
                            </SheetClose>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <MobileLink to="/#industries">Industries</MobileLink>
                <MobileLink to="/about">About Us</MobileLink>
                <MobileLink to="/pricing">Pricing</MobileLink>
                <MobileLink to="/contact">Contact</MobileLink>
              </div>
              <div className="p-4 mt-2 flex flex-col gap-3 border-t border-slate-200">
                <CTALink to="/contact" variant="secondary" size="md">Talk to an Expert</CTALink>
                <CTALink to="/pricing" variant="primary" size="md" withArrow>Get Started</CTALink>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      cn(
        "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        isActive && to === "/" ? "text-[var(--et-primary)]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
      )
    }
  >
    {children}
  </NavLink>
);

const MobileLink = ({ to, children }) => (
  <SheetClose asChild>
    <Link to={to} className="block rounded-lg px-3 py-3 text-sm font-medium text-slate-800 hover:bg-slate-50">
      {children}
    </Link>
  </SheetClose>
);
